import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Plus, ChevronDown, Crown, FileText, ShoppingCart, Package, UserSquare, ClipboardList, Receipt, Loader2, Menu } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from './context/AuthContext';
import { useMobileNav } from './context/MobileNavContext';
import { AiOmnibarPanel } from './AiOmnibarPanel';
import { UpgradeProModal } from './upgrade/UpgradeProModal';
import { KeyboardShortcutsDialog } from './KeyboardShortcutsDialog';
import { useThemeMode } from '../hooks/useThemeMode';
import { useCachedImage } from '../hooks/useCachedImage';
import { ActivityMenu } from './topbar/ActivityMenu';
import { BroadcastsMenu } from './topbar/BroadcastsMenu';
import { ProfileMenu } from './topbar/ProfileMenu';
import { useDismissibleFeed } from '../hooks/useDismissibleFeed';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Static destinations — kept only for "no query" idle hints. The real
// global search hits the indexed backend endpoint /api/search/global.
const QUICK_NAV = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Inventory', path: '/inventory' },
  { label: 'GST Reports', path: '/reports/gst-compliance' },
  { label: 'Settings', path: '/settings' },
];

const QUICK_CREATE_DOCS = [
  { label: 'Invoice', path: '/invoices?action=create', icon: FileText, testId: 'topbar-create-invoice' },
  { label: 'Quotation', path: '/quotations?action=create', icon: ClipboardList, testId: 'topbar-create-quotation' },
  { label: 'Proforma Invoice', path: '/proforma?action=create', icon: Receipt, testId: 'topbar-create-proforma' },
  { label: 'Credit Note', path: '/credit-notes?action=create', icon: Receipt, testId: 'topbar-create-credit-note' },
  { label: 'Purchase Order', path: '/purchase-orders?action=create', icon: ShoppingCart, testId: 'topbar-create-purchase-order' },
  { label: 'Purchase Invoice', path: '/purchase-invoices?action=create', icon: Receipt, testId: 'topbar-create-purchase-invoice' },
  { label: 'Debit Note', path: '/debit-notes?action=create', icon: Receipt, testId: 'topbar-create-debit-note' },
];

const QUICK_CREATE_MASTERS = [
  { label: 'Customer', path: '/customers?action=create', icon: UserSquare, testId: 'topbar-create-customer' },
  { label: 'Vendor', path: '/vendors?action=create', icon: UserSquare, testId: 'topbar-create-vendor' },
  { label: 'Product', path: '/products?action=create', icon: Package, testId: 'topbar-create-product' },
];

function resolveAssetUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url}`;
}

// Menu identifiers — single source of truth for which dropdown is open.
// A single string slot replaces N booleans so click-toggle semantics are
// trivial: same menu → null; other menu → that name.
const MENUS = { SEARCH: 'search', CREATE: 'create', ACTIVITY: 'activity', BROADCASTS: 'broadcasts', PROFILE: 'profile' };

export function TopBar() {
  const { user, logout } = useAuth();
  const { toggle: toggleMobileNav } = useMobileNav();
  const navigate = useNavigate();
  const { isDark, toggle: toggleTheme } = useThemeMode();

  const [branding, setBranding] = useState({ brand_logo_url: '', login_title_text: 'Rupiwise' });
  const [org, setOrg] = useState(null);
  const [team, setTeam] = useState(null);
  const [activity, setActivity] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  // ---- AI Omnibar state ----
  // `aiState` drives the inline panel rendered by <AiOmnibarPanel/>.
  // It transitions: null → 'busy' → ('success'|'ambiguous'|'knowledge'|'error') → null.
  const [aiState, setAiState] = useState(null);
  const [aiCandidates, setAiCandidates] = useState([]);
  const [aiAmbiguousField, setAiAmbiguousField] = useState(null);
  const [aiAmbiguousParsed, setAiAmbiguousParsed] = useState(null);
  const [aiKnowledge, setAiKnowledge] = useState([]);
  const [aiSuccessMessage, setAiSuccessMessage] = useState('');
  const [aiErrorMessage, setAiErrorMessage] = useState('');
  // Backend-indexed search state. `categories` is {invoices, customers, products}.
  const [searchCategories, setSearchCategories] = useState({ invoices: [], customers: [], products: [] });
  const [searchLoading, setSearchLoading] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);

  // Anchor refs for click-outside detection.
  const searchRef = useRef(null);
  const createRef = useRef(null);
  const activityRef = useRef(null);
  const broadcastsRef = useRef(null);
  const profileRef = useRef(null);

  // ---- Fetch branding + org context (one-time, fire-and-forget). ----
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/site-config/public`)
      .then((r) => { if (!cancelled) { setBranding(r.data || {}); setLogoBroken(false); } })
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/organization/me`)
      .then((r) => { if (!cancelled) setOrg(r.data?.organization || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const refreshTeam = useCallback(() => {
    if (!user) return;
    axios.get(`${BACKEND_URL}/api/team/members`)
      .then((r) => setTeam({
        seats_used: r.data?.seats_used || 0,
        max_users: r.data?.organization?.max_users || 0,
        at_cap: !!r.data?.at_cap,
      }))
      .catch(() => {});
  }, [user]);
  useEffect(() => { refreshTeam(); }, [refreshTeam]);

  // ---- Notifications: tenant activity (last 24h) + admin broadcasts ----
  const refreshActivity = useCallback(() => {
    if (!user) return;
    axios.get(`${BACKEND_URL}/api/notifications/recent`)
      .then((r) => setActivity(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => setActivity([]));
  }, [user]);

  const refreshBroadcasts = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/notifications/public`)
      .then((r) => setBroadcasts(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => setBroadcasts([]));
  }, []);

  useEffect(() => {
    refreshActivity();
    refreshBroadcasts();
    const a = setInterval(refreshActivity, 60_000);
    const b = setInterval(refreshBroadcasts, 5 * 60_000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [refreshActivity, refreshBroadcasts]);

  // Per-feed dismissed + viewed tracking. Both feeds:
  //   - hide items the user X-dismissed (persisted in localStorage)
  //   - drop the unread badge to 0 the moment the dropdown opens
  // Identity rule for activity items must be stable even when the API
  // returns no `.id` for a synthetic event (rare but possible), hence
  // the composite `${type}-${id|created_at|idx}`.
  const broadcastsFeed = useDismissibleFeed(
    broadcasts,
    'broadcasts',
    (b, i) => b?.id || `bcast-${i}`,
    { serverSync: true, feedKey: 'broadcasts' },
  );
  const activityFeed = useDismissibleFeed(
    activity,
    'activity',
    (a, i) => `${a?.type || 'x'}-${a?.id || a?.created_at || i}`,
    { serverSync: true, feedKey: 'activity' },
  );

  // Reset broken-image fallback whenever the chosen URL changes
  // (light↔dark switch may swap the src to a freshly-uploaded variant).
  useEffect(() => { setLogoBroken(false); }, [branding.brand_logo_url, branding.brand_logo_dark_url, isDark]);

  // ---- Click-outside: collapse whichever menu is open. ----
  useEffect(() => {
    function handler(e) {
      const refs = [searchRef, createRef, activityRef, broadcastsRef, profileRef];
      const insideAny = refs.some((r) => r.current && r.current.contains(e.target));
      if (!insideAny) setOpenMenu(null);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Toggle: if the requested menu is already open, collapse it; otherwise
  // open it (which implicitly closes any other open menu). This single
  // function replaces N closeAll()+setX(v=>!v) call-sites and fixes the
  // "second click doesn't close" bug caused by the old batched updaters.
  const toggleMenu = useCallback((name) => {
    setOpenMenu((cur) => (cur === name ? null : name));
  }, []);

  // ---- Backend-indexed search results (debounced 250ms). ----
  const trimmed = searchQuery.trim();
  useEffect(() => {
    if (!trimmed) {
      setSearchCategories({ invoices: [], customers: [], products: [] });
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const controller = new AbortController();
    const t = setTimeout(() => {
      axios
        .get(`${BACKEND_URL}/api/search/global`, {
          params: { q: trimmed },
          signal: controller.signal,
        })
        .then((r) => {
          setSearchCategories({
            invoices: Array.isArray(r.data?.invoices) ? r.data.invoices : [],
            customers: Array.isArray(r.data?.customers) ? r.data.customers : [],
            products: Array.isArray(r.data?.products) ? r.data.products : [],
          });
        })
        .catch(() => {
          setSearchCategories({ invoices: [], customers: [], products: [] });
        })
        .finally(() => setSearchLoading(false));
    }, 250);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [trimmed]);

  const totalResults =
    searchCategories.invoices.length +
    searchCategories.customers.length +
    searchCategories.products.length;

  // First hit across all categories — used for "press Enter to open".
  const firstHit = useMemo(() => {
    if (searchCategories.invoices[0]) {
      return { path: `/invoices`, label: searchCategories.invoices[0].invoice_number };
    }
    if (searchCategories.customers[0]) {
      return { path: `/customers`, label: searchCategories.customers[0].name };
    }
    if (searchCategories.products[0]) {
      return { path: `/products`, label: searchCategories.products[0].name };
    }
    return null;
  }, [searchCategories]);

  const onSearchSubmit = async (e) => {
    e.preventDefault();
    const q = (searchQuery || '').trim();
    if (!q) return;

    // AI Omnibar — every Enter press is interpreted as a natural-language
    // command first. If the LLM can't make sense of it we fall back to the
    // knowledgebase lookup, and finally to the existing entity-search hit.
    setOpenMenu(MENUS.SEARCH);  // keep the parent dropdown anchored
    setAiState('busy');
    setAiCandidates([]);
    setAiKnowledge([]);
    try {
      const res = await axios.post(`${API}/v1/ai/execute-command`, { text: q });
      const data = res.data || {};

      if (data.status === 'ok') {
        // Show a brief in-panel success, fire the toast, then redirect.
        const docType = data.doc_type || 'invoice';
        const docLabel = { quotation: 'Quotation', purchase_invoice: 'Purchase Invoice' }[docType] || 'Invoice';
        setAiState('success');
        setAiSuccessMessage(data.message || `${docLabel} ${data.invoice_number} generated`);
        const pct = data.parsed?.discount_percentage || 0;
        toast.success(
          `${docLabel} ${data.invoice_number} generated${pct ? ` with ${pct}% off!` : '!'}`,
        );
        // Give the success panel a moment to render before navigating away.
        setTimeout(() => {
          setOpenMenu(null);
          setAiState(null);
          setSearchQuery('');
          if (data.invoice_id) {
            // Route to the right list-scoped detail path per doc type.
            const targetPath = {
              quotation: '/quotations',
              purchase_invoice: '/purchases/invoices',
            }[docType] || `/sales/invoices/${data.invoice_id}`;
            navigate(targetPath);
          }
        }, 600);
        return;
      }

      if (data.status === 'ambiguous_match') {
        setAiState('ambiguous');
        setAiAmbiguousField(data.ambiguous_field || 'customer');
        setAiAmbiguousParsed(data.parsed || null);
        setAiCandidates(data.candidates || []);
        return;
      }

      // parsed_only / unknown — try the knowledge lookup so the user
      // still gets a useful answer for "how do I" type questions.
      const kbRes = await axios.get(`${API}/v1/ai/knowledge-lookup`, { params: { q } });
      const articles = kbRes.data || [];
      if (articles.length > 0) {
        setAiState('knowledge');
        setAiKnowledge(articles);
        return;
      }

      // Last fallback — if the existing entity search has a hit, navigate there.
      if (firstHit) {
        navigate(firstHit.path);
        setSearchQuery('');
        setOpenMenu(null);
        setAiState(null);
        return;
      }

      setAiState('error');
      setAiErrorMessage(data.message || 'Nothing matched. Try rephrasing your command.');
    } catch (err) {
      setAiState('error');
      setAiErrorMessage(err?.response?.data?.detail || err?.message || 'AI request failed — please retry.');
    }
  };

  // Ambiguity resolution — substitute the chosen entity's canonical name
  // into the original query so the AI re-parse hits an exact match. For
  // product:N replacements we naively swap the parsed product_name_string
  // for the picked candidate name. This is intentionally simple — 95%
  // of real-world utterances resolve cleanly with this approach.
  const handleCandidatePick = async (candidate) => {
    const parsed = aiAmbiguousParsed || {};
    const field = aiAmbiguousField || 'customer';
    let rewritten = searchQuery;
    try {
      if (field === 'customer' || field === 'vendor') {
        const old = (parsed.target_client_string || '').trim();
        if (old) rewritten = rewritten.replace(new RegExp(old.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i'), candidate.name);
      } else if (field.startsWith('product:')) {
        const idx = Number(field.split(':')[1] || 0);
        const item = (parsed.line_items || [])[idx];
        const old = (item?.product_name_string || '').trim();
        if (old) rewritten = rewritten.replace(new RegExp(old.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i'), candidate.name);
      }
    } catch { /* fall through, will resubmit verbatim */ }
    setSearchQuery(rewritten);
    // Re-fire the same submit handler programmatically.
    await onSearchSubmit({ preventDefault: () => {} });
  };

  const goTo = (path) => {
    navigate(path);
    setSearchQuery('');
    setOpenMenu(null);
  };

  const openTeamSettings = () => {
    sessionStorage.setItem('settings.initialTab', 'team');
    navigate('/settings');
    setOpenMenu(null);
  };

  const isOwner = (user?.org_role || '') === 'owner';
  const showUpgrade = org?.license_tier === 'standard' && isOwner;
  const showSeats = isOwner && team && team.max_users > 0;
  const logoUrl = resolveAssetUrl(
    (isDark && branding.brand_logo_dark_url) || branding.brand_logo_url,
  );
  // localStorage-backed image byte cache — needed because the K8s ingress
  // strips `Cache-Control: max-age=...` and forces a re-fetch every reload.
  const cachedLogo = useCachedImage(logoUrl);
  const effectiveLogoSrc = cachedLogo || logoUrl;
  const brandName = branding.login_title_text || 'Rupiwise';

  return (
    <header
      data-testid="topbar"
      className="fixed top-0 left-0 right-0 h-14 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 z-50 flex items-center px-4 sm:px-6"
      style={{ boxShadow: '0 1px 2px 0 rgba(0,0,0,0.04)' }}
    >
      {/* ============ LEFT: hamburger (mobile) + Brand logo ============ */}
      <button
        type="button"
        onClick={toggleMobileNav}
        data-testid="mobile-nav-toggle"
        aria-label="Open navigation menu"
        className="md:hidden w-11 h-11 -ml-2 mr-1 inline-flex items-center justify-center rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 transition-colors"
      >
        <Menu className="w-5 h-5" />
      </button>
      <div
        className="flex items-center cursor-pointer select-none md:w-64 pr-2 md:pr-4 flex-shrink-0"
        onClick={() => navigate('/dashboard')}
        data-testid="topbar-brand"
      >
        {logoUrl && !logoBroken ? (
          <img
            src={effectiveLogoSrc}
            alt=""
            data-testid="topbar-brand-logo"
            onError={() => setLogoBroken(true)}
            className="max-h-10 w-auto object-contain"
          />
        ) : (
          <div
            data-testid="topbar-brand-fallback"
            className="w-9 h-9 rounded-md flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: 'var(--theme-primary)' }}
          >
            {(brandName || 'B').charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* ============ CENTER: Global search ============ */}
      <div className="flex-1 flex justify-center px-2 sm:px-4 min-w-0">
        <div className="relative w-full md:max-w-xl" ref={searchRef}>
          <form onSubmit={onSearchSubmit}>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                style={{ color: 'var(--theme-primary)' }}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setOpenMenu(MENUS.SEARCH);
                  // Any keystroke after a settled AI result resets the
                  // panel so the user gets fresh feedback on re-submit.
                  if (aiState && aiState !== 'busy') setAiState(null);
                }}
                onFocus={() => setOpenMenu(MENUS.SEARCH)}
                placeholder="Ask AI or search — e.g. 'Bill ACME 5 widgets, payment received'"
                data-testid="topbar-search-input"
                className="w-full h-11 sm:h-9 pl-9 pr-3 rounded-lg bg-slate-100 dark:bg-slate-700 border border-transparent text-sm text-slate-700 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-700 focus:border-slate-300 dark:focus:border-slate-500 transition-all"
              />
            </div>
          </form>
          {/* AI Omnibar panel — appears for AI flows (busy/ambiguous/knowledge/success/error).
              Hides the entity-search dropdown while active so the two don't overlap. */}
          {aiState && (
            <AiOmnibarPanel
              state={aiState}
              knowledge={aiKnowledge}
              candidates={aiCandidates}
              ambiguousField={aiAmbiguousField}
              successMessage={aiSuccessMessage}
              errorMessage={aiErrorMessage}
              onClose={() => { setAiState(null); setAiKnowledge([]); setAiCandidates([]); }}
              onCandidatePick={handleCandidatePick}
            />
          )}
          {openMenu === MENUS.SEARCH && trimmed && !aiState && (
            <div
              className="absolute top-full mt-2 left-0 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-1.5 max-h-[28rem] overflow-y-auto z-50"
              data-testid="topbar-search-results"
            >
              {searchLoading && (
                <div className="flex items-center justify-center gap-2 py-4 text-xs text-slate-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                </div>
              )}
              {!searchLoading && totalResults === 0 && (
                <div className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400 text-center">
                  No matches for “{trimmed}”
                </div>
              )}
              {/* --- Invoices --- */}
              {searchCategories.invoices.length > 0 && (
                <div data-testid="search-group-invoices">
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-700/40">
                    Invoices
                  </div>
                  {searchCategories.invoices.map((it) => (
                    <button
                      key={`inv-${it.id}`}
                      type="button"
                      onClick={() => goTo('/invoices')}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left"
                      data-testid={`search-invoice-${it.id}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <FileText className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                        <span className="font-mono text-xs truncate">{it.invoice_number}</span>
                        <span className="text-slate-500 dark:text-slate-400 truncate">— {it.customer_name}</span>
                      </span>
                      <span className="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">
                        ₹{(it.total_amount || 0).toLocaleString('en-IN')}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {/* --- Customers --- */}
              {searchCategories.customers.length > 0 && (
                <div data-testid="search-group-customers">
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-700/40">
                    Customers
                  </div>
                  {searchCategories.customers.map((it) => (
                    <button
                      key={`cust-${it.id}`}
                      type="button"
                      onClick={() => goTo('/customers')}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left"
                      data-testid={`search-customer-${it.id}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <UserSquare className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                        <span className="font-medium truncate">{it.name}</span>
                      </span>
                      <span className="text-xs text-slate-400 dark:text-slate-500 truncate flex-shrink-0">
                        {it.phone || it.email || it.city || ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {/* --- Products --- */}
              {searchCategories.products.length > 0 && (
                <div data-testid="search-group-products">
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-700/40">
                    Products
                  </div>
                  {searchCategories.products.map((it) => (
                    <button
                      key={`prod-${it.id}`}
                      type="button"
                      onClick={() => goTo('/products')}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left"
                      data-testid={`search-product-${it.id}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Package className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                        <span className="font-medium truncate">{it.name}</span>
                        {it.product_id && (
                          <span className="font-mono text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">
                            ({it.product_id})
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">
                        {it.category || ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {/* --- Quick nav hints (only when no live results) --- */}
              {!searchLoading && totalResults === 0 && trimmed && (
                <div className="border-t border-slate-100 dark:border-slate-700 pt-1.5">
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Quick navigation
                  </div>
                  {QUICK_NAV.map((r) => (
                    <button
                      key={r.path}
                      type="button"
                      onClick={() => goTo(r.path)}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left"
                    >
                      <span className="font-medium">{r.label}</span>
                      <span className="text-xs text-slate-400 dark:text-slate-500">{r.path}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ============ RIGHT: Actions + Notifications + Profile ============ */}
      <div className="flex items-center gap-1.5">
        {/* Quick Create */}
        <div className="relative" ref={createRef}>
          <button
            type="button"
            onClick={() => toggleMenu(MENUS.CREATE)}
            data-testid="topbar-create-btn"
            title="Quick create"
            className="h-9 w-9 sm:w-auto sm:px-3 inline-flex items-center justify-center gap-1.5 rounded-full sm:rounded-lg text-white transition-all hover:opacity-90 active:scale-95"
            style={{ backgroundColor: 'var(--theme-primary)' }}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline text-sm font-semibold">Create</span>
            <ChevronDown className="hidden sm:inline w-3.5 h-3.5 opacity-80" />
          </button>
          {openMenu === MENUS.CREATE && (
            <div
              className="absolute right-0 top-full mt-2 w-60 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-1.5 z-50"
              data-testid="topbar-create-menu"
            >
              <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Documents
              </div>
              {QUICK_CREATE_DOCS.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => goTo(item.path)}
                    data-testid={item.testId}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2.5"
                  >
                    <Icon className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
              <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Master Data
              </div>
              {QUICK_CREATE_MASTERS.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => goTo(item.path)}
                    data-testid={item.testId}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2.5"
                  >
                    <Icon className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <ActivityMenu
          ref={activityRef}
          open={openMenu === MENUS.ACTIVITY}
          onToggle={() => {
            // Mark-as-viewed when the dropdown is about to OPEN (not when
            // closing) so the badge clears on the user's first peek.
            if (openMenu !== MENUS.ACTIVITY) activityFeed.markAllAsViewed();
            toggleMenu(MENUS.ACTIVITY);
          }}
          items={activityFeed.visibleItems}
          unreadCount={activityFeed.unreadCount}
          onDismiss={activityFeed.dismissOne}
          getId={(a, i) => `${a?.type || 'x'}-${a?.id || a?.created_at || i}`}
        />

        <BroadcastsMenu
          ref={broadcastsRef}
          open={openMenu === MENUS.BROADCASTS}
          onToggle={() => {
            if (openMenu !== MENUS.BROADCASTS) broadcastsFeed.markAllAsViewed();
            toggleMenu(MENUS.BROADCASTS);
          }}
          items={broadcastsFeed.visibleItems}
          unreadCount={broadcastsFeed.unreadCount}
          onDismiss={broadcastsFeed.dismissOne}
          getId={(b, i) => b?.id || `bcast-${i}`}
        />

        {/* Upgrade-to-Pro pill (owners on Standard tier only) */}
        {showUpgrade && (
          <button
            type="button"
            data-testid="topbar-upgrade-btn"
            onClick={() => setUpgradeOpen(true)}
            className="hidden md:inline-flex h-9 px-2.5 items-center gap-1.5 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 transition-all"
          >
            <Crown className="w-3.5 h-3.5 text-amber-700" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-amber-800">Upgrade</span>
          </button>
        )}

        <ProfileMenu
          ref={profileRef}
          open={openMenu === MENUS.PROFILE}
          onToggle={() => toggleMenu(MENUS.PROFILE)}
          user={user}
          org={org}
          team={team}
          showSeats={showSeats}
          isOwner={isOwner}
          isDark={isDark}
          onToggleTheme={toggleTheme}
          onOpenShortcuts={() => { setOpenMenu(null); setShortcutsOpen(true); }}
          onOpenTeam={openTeamSettings}
          onOpenSettings={() => goTo('/settings')}
          onHelp={() => { setOpenMenu(null); navigate('/dashboard/support'); }}
          onLogout={() => { setOpenMenu(null); logout(); }}
        />
      </div>

      <UpgradeProModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        user={user}
        onUpgraded={(updatedOrg) => setOrg(updatedOrg)}
      />

      <KeyboardShortcutsDialog
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
      />
    </header>
  );
}
